import { Fragment, useEffect, useRef, useState } from "react";
import {
  type LucideIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  SearchIcon,
} from "lucide-react";
import { endOfWeek, format, startOfWeek, subDays, subWeeks } from "date-fns";
import { Area, AreaChart, Line, LineChart, XAxis } from "recharts";

import { Column, Row } from "@/components";
import { Card } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetPortfolioTrends } from "@/hooks/queries/portfolio/useGetPortfolioTrends";
import { PortfolioTrendsType, PortfolioTrendWindow } from "@/types";
import { cn } from "@/lib/utils";

const PORTFOLIO_TREND_CHART_COLORS = {
  velocity: "#0D9488",
  capacity: "#2563EB",
  incoming: "#9333EA",
  added: "#06B6D4",
  resolved: "#F97316",
} as const;

const TREND_TIME_WINDOWS: PortfolioTrendWindow[] = ["week", "month", "quarter"];

const TREND_TIME_WINDOW_LABELS: Record<PortfolioTrendWindow, string> = {
  week: "Week",
  month: "Month",
  quarter: "Quarter",
};

const SPARKLINE_VIEW_WIDTH = 180;
const SPARKLINE_VIEW_HEIGHT = 100;

type TrendSentiment = "positive" | "negative";

interface TrendSeriesConfig {
  label: string;
  color: string;
}

/**
 * Static, client-owned config for each trend card. The API only sends numbers
 * (see {@link PortfolioTrends}); titles, labels, colors, sentiment, and how to
 * derive the headline value all live here.
 */
interface TrendCardConfig {
  id: string;
  title: string;
  /** Optional icon shown before the title (e.g. the bottleneck card). */
  icon?: LucideIcon;
  /** Drives the change badge color. Omit for cards with no change badge. */
  sentiment?: TrendSentiment;
  /** Trailing footer label after the legend. Omit to show just the legend. */
  comparisonLabel?: string;
  /** Overrides the footer legend label and varies it with the selected window. */
  footerLabel?: (window: PortfolioTrendWindow) => string;
  /** One entry → single line + area; two+ → multi-line. */
  series: TrendSeriesConfig[];
  /** Order the legend (by first point) and tooltip (by hovered point) high→low. */
  orderByValue?: boolean;
  /** Render style: area for the headline card, line for comparisons. */
  chartType: "area" | "line";
  /** Pull this card's number arrays out of the response, in series order.
   *  Omit `changePercent` for cards without a change badge. */
  select: (trends: PortfolioTrendsType) => {
    values: number[][];
    changePercent?: number;
  };
  /** Build the headline. Omit for cards with no headline value (chart only). */
  formatValue?: (
    latest: number[],
    ctx: { values: number[][]; window: PortfolioTrendWindow },
  ) => string;
  /** Placeholder wave shape shown while loading (purely decorative). */
  skeletonWave: number[];
}

const TREND_CARD_CONFIGS: TrendCardConfig[] = [
  {
    id: "placement-velocity",
    title: "Placement velocity",
    sentiment: "positive",
    footerLabel: (window) =>
      window === "week" ? "Placements per day" : "Placements per week",
    chartType: "area",
    series: [
      {
        label: "Placements per week",
        color: PORTFOLIO_TREND_CHART_COLORS.velocity,
      },
    ],
    select: (trends) => ({
      values: [trends.placementVelocity.points],
      changePercent: trends.placementVelocity.changePercent,
    }),
    formatValue: (_latest, { values, window }) => {
      const points = values[0];
      const avg = points.length
        ? points.reduce((sum, point) => sum + point, 0) / points.length
        : 0;
      const unit = window === "week" ? "/day" : "/wk";
      return `${avg.toFixed(1)}${unit}`;
    },
    // Steady climb.
    skeletonWave: [3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 9],
  },
  {
    id: "critical-at-risk",
    title: "Critical & at-risk over time",
    // Up = more alerts = bad; down = fewer = good. Invert vs the usual sentiment.
    sentiment: "negative",
    chartType: "line",
    series: [
      { label: "Critical", color: "#D14343" },
      { label: "At-risk", color: "#E67E22" },
    ],
    select: (trends) => ({
      values: [
        trends.criticalAndAtRisk.critical,
        trends.criticalAndAtRisk.atRisk,
      ],
      changePercent: trends.criticalAndAtRisk.changePercent,
    }),
    formatValue: (latest) => {
      const total = (latest[0] ?? 0) + (latest[1] ?? 0);
      return `${total.toString()} jobs`;
    },
    // Gentle step-down (improving trend).
    skeletonWave: [8, 7.5, 8, 7, 7.5, 6.5, 7, 6, 6.5, 5.5, 6, 5],
  },
  {
    id: "bottleneck-trend",
    title: "Bottlenecks over time",
    // No headline value / change %. The icon takes the change-badge slot.
    icon: SearchIcon,
    chartType: "line",
    // Green screening — opposite the blue + orange pair so all three read apart.
    series: [
      { label: "Customer", color: "#2563EB" },
      { label: "Screening", color: "#16A34A" },
      { label: "Sourcing", color: "#F97316" },
    ],
    select: (trends) => ({
      values: [
        trends.bottlenecks.customer,
        trends.bottlenecks.screening,
        trends.bottlenecks.sourcing,
      ],
    }),
    orderByValue: true,
    formatValue: () => {
      return "Sourcing";
    },
    skeletonWave: [4, 5, 4.5, 6, 5.5, 6.5, 6, 7, 6.5, 7.5, 7, 8],
  },
  {
    id: "reqs-added-vs-resolved",
    title: "Reqs added vs resolved",
    sentiment: "positive",
    comparisonLabel: "portfolio growth this period",
    chartType: "line",
    series: [
      { label: "Added & expanded", color: PORTFOLIO_TREND_CHART_COLORS.added },
      {
        label: "Filled, washed & lost",
        color: PORTFOLIO_TREND_CHART_COLORS.resolved,
      },
    ],
    select: (trends) => ({
      values: [
        trends.reqsAddedVsResolved.added,
        trends.reqsAddedVsResolved.resolved,
      ],
      changePercent: trends.reqsAddedVsResolved.changePercent,
    }),
    formatValue: (_latest, { values }) => {
      // Net diff over the window: total reqs added − total reqs resolved.
      const [added, resolved] = values;
      const sum = (series: number[]) =>
        series.reduce((total, point) => total + point, 0);
      const net = sum(added) - sum(resolved);
      return `Net ${net >= 0 ? "+" : ""}${net.toString()} reqs`;
    },
    // Choppy sawtooth.
    skeletonWave: [4, 7, 4.5, 8, 5, 7.5, 4.5, 8.5, 5, 9, 5.5, 8],
  },
];

const PORTFOLIO_OVERVIEW_CARD_CLASS =
  "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden shadow-none px-5 pb-6 pt-5";

const buildSparklinePoints = (
  values: number[],
  width: number,
  height: number,
) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return values.map((value, index) => ({
    x: values.length === 1 ? width / 2 : (index / (values.length - 1)) * width,
    y: height - ((value - min) / range) * (height - 4) - 2,
  }));
};

const pointsToLinePath = (points: { x: number; y: number }[]) =>
  points
    .map((point, index) =>
      index === 0
        ? `M ${point.x.toString()} ${point.y.toString()}`
        : `L ${point.x.toString()} ${point.y.toString()}`,
    )
    .join(" ");

const pointsToAreaPath = (
  points: { x: number; y: number }[],
  height: number,
) => {
  if (points.length === 0) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${pointsToLinePath(points)} L ${last.x.toString()} ${height.toString()} L ${first.x.toString()} ${height.toString()} Z`;
};

const SPARKLINE_VIEW_BOX = `0 0 ${SPARKLINE_VIEW_WIDTH.toString()} ${SPARKLINE_VIEW_HEIGHT.toString()}`;

/** Recharts wants row-per-x-point; pivot the per-series arrays into `{ s0, s1 }`. */
const buildChartData = (values: number[][], labels: string[]) => {
  const length = Math.max(0, ...values.map((series) => series.length));
  return Array.from({ length }, (_, index) => {
    const point: Record<string, number | string> = { label: labels[index] };
    values.forEach((series, seriesIndex) => {
      point[`s${seriesIndex.toString()}`] = series[index] ?? 0;
    });
    return point;
  });
};

/**
 * Derives a human label for each x-point. The API sends numbers only, so dates
 * are relative to today: the last point is the most recent period. `week` and
 * month charts with daily buckets → one point per day ("June 8th"); weekly
 * bucket charts → one point per week ("Jun 2 - Jun 8").
 */
const buildPointLabels = (
  count: number,
  window: PortfolioTrendWindow,
): string[] => {
  const today = new Date();
  const isDailyBucket = window === "week" || (window === "month" && count > 13);

  return Array.from({ length: count }, (_, index) => {
    const stepsBack = count - 1 - index;
    if (isDailyBucket) {
      return format(subDays(today, stepsBack), "MMMM do");
    }
    const weekStart = startOfWeek(subWeeks(today, stepsBack), {
      weekStartsOn: 1,
    });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d")}`;
  });
};

// Tooltip content that orders its rows by value (high→low) for the hovered point.
const ValueSortedTooltipContent = (props: {
  active?: boolean;
  label?: string | number;
  payload?: { color?: string; name?: string; value?: number | string }[];
}) => {
  const sortedPayload = props.payload
    ? [...props.payload].sort((a, b) => Number(b.value) - Number(a.value))
    : props.payload;
  return <ChartTooltipContent {...props} payload={sortedPayload} />;
};

export const PortfolioTrends = ({
  isDisabled = false,
  stafferId,
  salesId,
}: {
  isDisabled?: boolean;
  stafferId?: string | null;
  salesId?: string | null;
}) => {
  const [timeWindow, setTimeWindow] = useState<PortfolioTrendWindow>("month");
  const trendsApi = useGetPortfolioTrends(timeWindow, stafferId, salesId);

  // Animate the charts only on their first appearance; once data has rendered,
  // keep them static so later re-renders (refetch, window change) don't replay.
  const isFirstRenderRef = useRef(true);
  const animateCharts = isFirstRenderRef.current;

  useEffect(() => {
    if (trendsApi.data) {
      isFirstRenderRef.current = false;
    }
  }, [trendsApi.data]);

  const renderTimeWindowSelect = () => (
    <Row className="shrink-0 items-center gap-2">
      <span className="text-xs font-medium text-slate-500">Time window</span>
      <Select
        value={timeWindow}
        onValueChange={(value) => {
          setTimeWindow(value as PortfolioTrendWindow);
        }}
      >
        <SelectTrigger className="h-8 w-[108px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TREND_TIME_WINDOWS.map((window) => (
            <SelectItem key={window} value={window}>
              {TREND_TIME_WINDOW_LABELS[window]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Row>
  );

  const renderChart = (
    config: {
      id: string;
      chartType: "area" | "line";
      series: TrendSeriesConfig[];
      footerLabel?: (window: PortfolioTrendWindow) => string;
      orderByValue?: boolean;
    },
    values: number[][],
  ) => {
    const length = Math.max(0, ...values.map((series) => series.length));
    const chartData = buildChartData(
      values,
      buildPointLabels(length, timeWindow),
    );
    // Single-series cards can override the legend label per window (e.g. velocity
    // reads "per day" on the week view); fall back to the static series label.
    const seriesLabel = (series: TrendSeriesConfig) =>
      config.footerLabel ? config.footerLabel(timeWindow) : series.label;
    const chartConfig: ChartConfig = Object.fromEntries(
      config.series.map((series, index) => [
        `s${index.toString()}`,
        { label: seriesLabel(series), color: series.color },
      ]),
    );
    const chartMargin = { top: 4, right: 4, bottom: 0, left: 4 };
    const tooltip = (
      <ChartTooltip
        cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
        content={
          config.orderByValue ? (
            <ValueSortedTooltipContent />
          ) : (
            <ChartTooltipContent />
          )
        }
      />
    );

    if (config.chartType === "area") {
      const gradientId = `fill-${config.id}`;
      const [series] = config.series;
      return (
        <ChartContainer
          key={`${config.id}-${timeWindow}`}
          config={chartConfig}
          className="h-full w-full"
        >
          <AreaChart data={chartData} margin={chartMargin}>
            <XAxis dataKey="label" hide />
            <defs>
              <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={series.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={series.color} stopOpacity={0} />
              </linearGradient>
            </defs>
            {tooltip}
            <Area
              dataKey="s0"
              name={seriesLabel(series)}
              type="monotone"
              stroke={series.color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              isAnimationActive={animateCharts}
            />
          </AreaChart>
        </ChartContainer>
      );
    }

    return (
      <ChartContainer
        key={`${config.id}-${timeWindow}`}
        config={chartConfig}
        className="h-full w-full"
      >
        <LineChart data={chartData} margin={chartMargin}>
          <XAxis dataKey="label" hide />
          {tooltip}
          {config.series.map((series, index) => (
            <Line
              key={series.label}
              dataKey={`s${index.toString()}`}
              name={seriesLabel(series)}
              type="monotone"
              stroke={series.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={animateCharts}
            />
          ))}
        </LineChart>
      </ChartContainer>
    );
  };

  // The sparkline itself is the loading shape: a pulsing slate wave + area,
  // shaped per card so each placeholder looks distinct.
  const renderSkeletonSparkline = (config: { skeletonWave: number[] }) => {
    const points = buildSparklinePoints(
      config.skeletonWave,
      SPARKLINE_VIEW_WIDTH,
      SPARKLINE_VIEW_HEIGHT,
    );

    return (
      <svg
        aria-hidden
        className="h-full w-full animate-pulse"
        preserveAspectRatio="none"
        viewBox={SPARKLINE_VIEW_BOX}
      >
        <path
          d={pointsToAreaPath(points, SPARKLINE_VIEW_HEIGHT)}
          className="fill-slate-100"
        />
        <path
          d={pointsToLinePath(points)}
          className="fill-none stroke-slate-200"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    );
  };

  const renderChangeBadge = (
    config: TrendCardConfig,
    changePercent: number,
  ) => {
    const isUp = changePercent >= 0;
    const isPositiveOutcome =
      (isUp && config.sentiment === "positive") ||
      (!isUp && config.sentiment === "negative");

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex cursor-default items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold",
              isPositiveOutcome
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700",
            )}
          >
            {isUp ? (
              <ArrowUpIcon className="h-3 w-3" strokeWidth={2.5} />
            ) : (
              <ArrowDownIcon className="h-3 w-3" strokeWidth={2.5} />
            )}
            {Math.abs(changePercent)}%
          </span>
        </TooltipTrigger>
        <TooltipContent>vs. previous {timeWindow}</TooltipContent>
      </Tooltip>
    );
  };

  const renderFooter = (config: TrendCardConfig, seriesOrder?: number[]) => (
    <Row className="flex-wrap items-center gap-1.5 pt-4 text-xs text-slate-500">
      {(seriesOrder ?? config.series.map((_, index) => index)).map(
        (seriesIndex, position) => {
          const series = config.series[seriesIndex];
          return (
            <Fragment key={series.label}>
              {position > 0 ? (
                <span className="text-slate-400" aria-hidden>
                  ·
                </span>
              ) : null}
              <span
                className="h-3 w-3 shrink-0 rounded-full ring-1 ring-slate-200"
                style={{ backgroundColor: series.color }}
                aria-hidden
              />
              <span>
                {config.footerLabel
                  ? config.footerLabel(timeWindow)
                  : series.label}
              </span>
            </Fragment>
          );
        },
      )}
      {config.comparisonLabel ? (
        <>
          <span className="text-slate-400" aria-hidden>
            ·
          </span>
          <span>{config.comparisonLabel}</span>
        </>
      ) : null}
    </Row>
  );

  // Title + legend are static config, so they render in both states; only the
  // headline, badge, and sparkline swap between skeleton and real data.
  const renderCardShell = (
    config: TrendCardConfig,
    headline: React.ReactNode,
    sparkline: React.ReactNode,
    seriesOrder?: number[],
  ) => (
    <Card key={config.id} className={PORTFOLIO_OVERVIEW_CARD_CLASS}>
      <Column className="min-h-0 min-w-0 flex-1 gap-2 overflow-hidden">
        <Row className="h-8 shrink-0 items-center justify-between gap-2">
          <p className="min-w-0 flex-1 truncate text-xs leading-none text-slate-500">
            {isDisabled && config.id === "reqs-added-vs-resolved"
              ? "Filter does not apply to this chart"
              : config.title}
          </p>
        </Row>
        <Row className="flex-wrap items-center gap-2">{headline}</Row>
        <div className="h-24 w-full min-w-0 shrink-0">{sparkline}</div>
        {renderFooter(config, seriesOrder)}
      </Column>
    </Card>
  );

  const renderTrendCard = (config: TrendCardConfig) => {
    if (trendsApi.isPending) {
      return renderCardShell(
        config,
        <>
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-5 w-12 rounded-md" />
        </>,
        renderSkeletonSparkline(config),
      );
    }
    if (!trendsApi.data) {
      return renderCardShell(
        config,
        <span className="text-sm font-medium text-slate-400">No data</span>,
        null,
      );
    }

    const { values, changePercent } = config.select(trendsApi.data);
    const latest = values.map((series) => series[series.length - 1] ?? 0);
    const Icon = config.icon;

    // Legend follows whichever line starts highest (first point), high→low.
    const seriesOrder = config.orderByValue
      ? values
          .map((series, index) => ({ index, first: series[0] ?? 0 }))
          .sort((a, b) => b.first - a.first)
          .map((entry) => entry.index)
      : undefined;

    return renderCardShell(
      config,
      <>
        {config.formatValue ? (
          <span className="text-xl font-semibold tabular-nums leading-none text-slate-900/80">
            {config.formatValue(latest, { values, window: timeWindow })}
          </span>
        ) : null}
        {changePercent != null ? (
          renderChangeBadge(config, changePercent)
        ) : Icon ? (
          <span className="inline-flex items-center rounded-md bg-amber-50 p-1 text-amber-700">
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </>,
      renderChart(config, values),
      seriesOrder,
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Column className="min-w-0 gap-2">
        <Row className="items-end justify-between">
          <span className="pb-2 text-xs font-semibold uppercase leading-none tracking-widest text-slate-500">
            Trends
          </span>
          {renderTimeWindowSelect()}
        </Row>
        <Row className="min-w-0 items-stretch gap-4">
          {TREND_CARD_CONFIGS.map((config, index) => (
            <div
              key={config.id}
              className={cn(
                "flex min-h-0 min-w-0 flex-1 flex-col",
                index === 3 && "transition-[filter,opacity] duration-200",
                index === 3 && isDisabled && "opacity-60 grayscale",
              )}
            >
              {renderTrendCard(config)}
            </div>
          ))}
        </Row>
      </Column>
    </TooltipProvider>
  );
};
